import moment from "moment";

import { CropFCPO, CropFCPORequest, CropItemData, CropPracticesRequestBody, FieldPracticeResponse, PracticeFCPO, PracticeFCPORequest } from "models";

import {
  FertilizerItemData,
  PRACTICE_TYPE,
  SeedingItemData,
  TillageItemData,
  TimelineCalendarItemsProps,
  UPDATE_TYPE,
} from "pages/app/field-timeline/components/FieldDetailsTimeline.tsx";

const updatePracticeApplicationTimes = (
  practices: PracticeFCPO[],
  oldCrop: CropFCPO,
  newCrop: CropFCPORequest,
  updateType: UPDATE_TYPE
): PracticeFCPORequest[] => {
  const oldTime: { start: string; end: string } = { start: oldCrop.seedingStartDate, end: oldCrop.harvestEndDate };
  const newTime: { start: string; end: string } = { start: newCrop.seedingStartDate, end: newCrop.harvestEndDate };

  return practices.map((practiceFcpo) => {
    const { isConfirmed: _, ...practice } = practiceFcpo;
    // STRAT DATE CHANGES
    if (updateType === UPDATE_TYPE.START) {
      if (practice.endDate === oldTime.start) {
        return { ...practice, endDate: newTime.start };
      }
      const startDifference = moment(practice.endDate).diff(oldTime.start, "days");
      const newPracticeTime = moment(newTime.start).add(startDifference, "days");
      // Including start and end date by using "[]"
      const isInCropRange = moment(newPracticeTime).isBetween(newTime.start, newTime.end, "day", "[]");
      if (isInCropRange) {
        return { ...practice, endDate: newPracticeTime.toISOString() };
      }
      return { ...practice, endDate: newTime.start };
    }
    // END DATE CHANGES
    if (updateType === UPDATE_TYPE.END) {
      if (practice.endDate === oldTime.end) {
        return { ...practice, endDate: newTime.end };
      }

      const practiceTimeDifference = moment(newTime.end).diff(practice.endDate, "days");
      if (practiceTimeDifference >= 0) return { ...practice, endDate: practice.endDate };
      // Including start and end date by using "[]"
      const isInCropRange = moment(practiceTimeDifference).isBetween(newTime.start, newTime.end, "days", "[]");
      if (isInCropRange) {
        return { ...practice, endDate: practice.endDate };
      }
      return { ...practice, endDate: newTime.end };
    }
    // MOVE (BOTH) DATE CHANGES
    if (updateType === UPDATE_TYPE.MOVE) {
      const daysMovedStart = moment(newTime.start).diff(oldTime.start, "days");
      return { ...practice, endDate: moment(practice.endDate).add(daysMovedStart, "days").toISOString() };
    }
    return practice;
  });
};

const validate = (itemToUpdate: TimelineCalendarItemsProps<CropItemData>, seedingItem: TimelineCalendarItemsProps<SeedingItemData>) => {
  if (!itemToUpdate.id) return false;
  if (!itemToUpdate.cropItemId) return false;
  if (!itemToUpdate.start_time) return false;
  if (!itemToUpdate.end_time) return false;
  if (!itemToUpdate.data) return false;
  if (itemToUpdate.data.hasResidue === undefined) return null;
  if (!itemToUpdate.data.cropType) return false;
  // Undersowing does not have seeding practice
  if (itemToUpdate.practiceType !== PRACTICE_TYPE.UNDERSOWING_CROP) {
    if (!seedingItem) return false;
    if (!seedingItem?.data?.seedingValue.seedingPracticeId) return false;
  }

  return true;
};
const handleCrop = (
  itemToUpdate: TimelineCalendarItemsProps<CropItemData>,
  allItems: TimelineCalendarItemsProps<CropItemData | TillageItemData | FertilizerItemData | SeedingItemData>[],
  fieldPracticeResponse: FieldPracticeResponse,
  updateType: UPDATE_TYPE
): CropPracticesRequestBody | null => {
  const oldCrop = fieldPracticeResponse.crops.find((crop) => crop.id === itemToUpdate.id);
  if (!oldCrop) return null;

  const foundCrop: TimelineCalendarItemsProps<CropItemData> = allItems.find(
    (item) => item?.id === itemToUpdate?.id
  ) as TimelineCalendarItemsProps<CropItemData>;

  if (!foundCrop) return null;

  const seedingItem: TimelineCalendarItemsProps<SeedingItemData> = allItems.find(
    (item) => item.id === `${PRACTICE_TYPE.SEEDING}-${itemToUpdate.id}`
  ) as TimelineCalendarItemsProps<SeedingItemData>;

  if (!validate(itemToUpdate, seedingItem)) return null;

  const newCrop: CropFCPORequest = {
    cropId: oldCrop.cropId,
    cropType: itemToUpdate.data.cropType,
    seedingStartDate: itemToUpdate.start_time.toISOString(),
    harvestEndDate: itemToUpdate.end_time.toISOString(),
    // Undersowing does not have seeding practice
    seedingPracticeType: seedingItem ? seedingItem.data?.seedingValue?.seedingPracticeId : "",
    hasResidue: itemToUpdate.data.hasResidue,
    seedingVerificationStatus: null,
  };

  if (itemToUpdate.practiceType === PRACTICE_TYPE.UNDERSOWING_CROP) delete newCrop.seedingPracticeType;

  const practices: PracticeFCPO[] = fieldPracticeResponse.practices.filter((practice) => practice.cropId === itemToUpdate.id);
  const updatedPractices = updatePracticeApplicationTimes(practices, oldCrop, newCrop, updateType);

  return {
    crop: newCrop,
    practices: updatedPractices,
    hasFertilizerManagement: null,
  };
};

export const createCropPracticesRequestBody = (
  itemToUpdate: TimelineCalendarItemsProps<CropItemData | TillageItemData | FertilizerItemData | SeedingItemData>,
  allItems: TimelineCalendarItemsProps<CropItemData | TillageItemData | FertilizerItemData | SeedingItemData>[],
  fieldPracticeResponse: FieldPracticeResponse,
  updateType: UPDATE_TYPE
): CropPracticesRequestBody | null => {
  // remove recommendations
  const itemsWithoutRecommendations = allItems.filter((item) => item.practiceType !== PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION);

  switch (itemToUpdate.practiceType) {
    case PRACTICE_TYPE.COVER_CROP:
    case PRACTICE_TYPE.MAIN_CROP:
    case PRACTICE_TYPE.UNDERSOWING_CROP:
      return handleCrop(itemToUpdate as TimelineCalendarItemsProps<CropItemData>, itemsWithoutRecommendations, fieldPracticeResponse, updateType);
    default:
      return null;
  }
};
